/**
 * ComplianceAlert — Non-HR UX layer (HUMANAI_UX_NONHR.md §3, หลักการ 4).
 *
 * "เตือนก่อนพลาด ไม่ใช่ปล่อยผิดแล้วโดนปรับ" — component กลางสำหรับเตือนเชิงรุก
 * เมื่อ action เสี่ยงผิดกฎหมาย/นโยบาย. ระบบเป็นคนรู้กฎหมายแทนผู้ใช้ที่ไม่ใช่ HR.
 *
 * 3 ระดับ:
 *   block — 🔴 ผิดกฎหมายแน่ / ทำต่อไม่ได้ (เตือนแรง)
 *   warn  — 🟡 เสี่ยง ทำต่อได้แต่ต้องรู้ตัว
 *   info  — ℹ️ ข้อควรรู้
 *
 * Portable: ใช้แค่ CSS class กลาง (card) + lucide + semantic color. ไม่ผูก
 * โดเมน payroll — product อื่นในเครือ reuse ได้ทันที (ดู HUMANAI_UX_NONHR §3).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

export type ComplianceLevel = 'block' | 'warn' | 'info';

export interface ComplianceItem {
  level: ComplianceLevel;
  /** หัวข้อสั้น ภาษาคน — ไม่ใช่ jargon (เช่น "ค่าจ้างต่ำกว่าค่าแรงขั้นต่ำ") */
  title: string;
  /** คำอธิบายเพิ่ม — บอกว่าจะเกิดอะไร/ต้องทำอะไร */
  detail?: ReactNode;
  /** ที่มา/อ้างอิงกฎหมาย (เช่น "พ.ร.บ.คุ้มครองแรงงาน ม.118") — สร้างความน่าเชื่อถือ */
  cite?: string;
  /** จำนวนรายการที่เข้าเงื่อนไข (เช่น 3 คน) */
  count?: number;
  /** ปุ่ม/ลิงก์ไปแก้ที่ต้นเหตุ */
  action?: { label: string; to?: string; onClick?: () => void };
}

const LEVEL_STYLE: Record<ComplianceLevel, { border: string; icon: string; Icon: typeof AlertCircle; chip: string }> = {
  block: { border: 'border-l-red-600', icon: 'text-red-600', Icon: AlertCircle, chip: 'bg-red-100 text-red-700' },
  warn: { border: 'border-l-amber-400', icon: 'text-amber-600', Icon: AlertTriangle, chip: 'bg-amber-100 text-amber-700' },
  info: { border: 'border-l-sky-400', icon: 'text-sky-600', Icon: Info, chip: 'bg-blue-100 text-blue-700' },
};

/** เตือนเดี่ยว 1 รายการ */
export function ComplianceAlert({ level, title, detail, cite, count, action }: ComplianceItem) {
  const s = LEVEL_STYLE[level];
  const { Icon } = s;
  return (
    <div className={`card border-l-4 ${s.border} px-3 py-3 flex gap-3`}>
      <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${s.icon}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          {typeof count === 'number' && (
            <span className={`badge ${s.chip} shrink-0 tabular-nums`}>{count}</span>
          )}
        </div>
        {detail && <p className="text-sm text-gray-600 mt-0.5">{detail}</p>}
        {cite && <p className="text-xs text-gray-400 mt-1">อ้างอิง: {cite}</p>}
        {action && (
          action.to ? (
            <Link to={action.to} className="inline-block text-sm font-medium text-teal mt-1.5 hover:underline">
              {action.label} →
            </Link>
          ) : (
            <button type="button" onClick={action.onClick} className="text-sm font-medium text-teal mt-1.5 hover:underline">
              {action.label} →
            </button>
          )
        )}
      </div>
    </div>
  );
}

/**
 * รายการเตือนหลายรายการ จัดกลุ่มตามระดับ (block ก่อน → warn → info).
 * `summary` = แถบสรุปบนสุด (เลือกใส่). ถ้า items ว่าง → render null.
 */
export function ComplianceAlertList({ items, summary }: { items: ComplianceItem[]; summary?: ReactNode }) {
  if (!items.length) return null;
  const order: ComplianceLevel[] = ['block', 'warn', 'info'];
  const sorted = [...items].sort((a, b) => order.indexOf(a.level) - order.indexOf(b.level));
  return (
    <div className="space-y-2">
      {summary}
      {sorted.map((it, i) => (
        <ComplianceAlert key={`${it.level}-${it.title}-${i}`} {...it} />
      ))}
    </div>
  );
}
